import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDraggableButton } from '../context/DraggableButtonContext';
import { clientToCssPixels, readViewportCssSize } from '../utils/displaySize';

export type DraggableButtonId = 'button1' | 'button2';

/**
 * The chrome a floating button has to stay below: the fixed header — which sits lower while a
 * top banner is showing — and the tab bar of a tabbed page.
 */
const TOP_CHROME_SELECTOR = '.main-header, .ant-tabs-nav';
/** Shortens by the banner's height when one appears, which is how the boundary learns about it. */
const LAYOUT_CONTAINER_SELECTOR = '.main-container';
const HEADER_HEIGHT_PX = 41;
const BUTTON_HEIGHT_PX = 64;
const LONG_PRESS_MS = 300;
/** A page's own tab bar opens no wider a gap than the page gutter; anything further down the
 *  page is content the buttons are meant to float over, not chrome. */
const CHROME_RUN_TOLERANCE_PX = 16;

/** Walks the run of chrome down from the top of the pane and returns where it ends. */
const measureTopChromeBottom = () =>
  [...document.querySelectorAll(TOP_CHROME_SELECTOR)].reduce((lowestEdge, element) => {
    const rect = element.getBoundingClientRect();
    const top = clientToCssPixels(rect.top);
    if (top > lowestEdge + CHROME_RUN_TOLERANCE_PX) return lowestEdge;
    return Math.max(lowestEdge, clientToCssPixels(rect.bottom));
  }, HEADER_HEIGHT_PX);

const useTopChromeBottom = () => {
  const [topChromeBottom, setTopChromeBottom] = useState(measureTopChromeBottom);

  /* The tab bar arrives with the route, so the boundary is re-read on every render; React drops
     the update when the number is unchanged. */
  useLayoutEffect(() => {
    setTopChromeBottom(measureTopChromeBottom());
  });

  useEffect(() => {
    const remeasure = () => setTopChromeBottom(measureTopChromeBottom());
    globalThis.addEventListener('resize', remeasure);

    const container = document.querySelector(LAYOUT_CONTAINER_SELECTOR);
    const observer =
      container && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(remeasure) : null;
    observer?.observe(container as Element);

    return () => {
      globalThis.removeEventListener('resize', remeasure);
      observer?.disconnect();
    };
  }, []);

  return topChromeBottom;
};

/**
 * Long-press dragging for the buttons parked against the left edge of the pane. They are kept
 * within the pane and below the header and tabs; a short press activates the button instead.
 */
export const useDraggableSideButton = (buttonId: DraggableButtonId, onActivate: () => void) => {
  const { positions, setPosition } = useDraggableButton();
  const topChromeBottom = useTopChromeBottom();
  const position = positions[buttonId];

  const [isDragging, setIsDragging] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopWatchingForRelease = useRef<(() => void) | null>(null);
  const hasDragged = useRef(false);

  const clampToPane = useCallback(
    (y: number) =>
      Math.min(Math.max(y, topChromeBottom), readViewportCssSize().height - BUTTON_HEIGHT_PX),
    [topChromeBottom],
  );

  /* A button parked under an earlier layout — before a banner pushed the header down, say —
     would end up sitting over that chrome, so it is pulled back as soon as the boundary moves. */
  useEffect(() => {
    const clamped = clampToPane(position);
    if (clamped !== position) setPosition(buttonId, clamped);
  }, [buttonId, clampToPane, position, setPosition]);

  const clearPressTimer = useCallback(() => {
    stopWatchingForRelease.current?.();
    stopWatchingForRelease.current = null;
    if (!pressTimer.current) return false;
    clearTimeout(pressTimer.current);
    pressTimer.current = null;
    return true;
  }, []);

  /* A release that lands off the button never reaches its own onMouseUp, so the press is watched
     for at the window too — otherwise the timer still fires and the button follows a cursor with
     no button held. The button's own handler runs first and takes this listener back off the
     window mid-dispatch, which is what keeps a short press an activation rather than a drag. */
  const watchForReleaseOffTheButton = useCallback(() => {
    const cancelPendingPress = () => clearPressTimer();
    globalThis.addEventListener('mouseup', cancelPendingPress);
    stopWatchingForRelease.current = () =>
      globalThis.removeEventListener('mouseup', cancelPendingPress);
  }, [clearPressTimer]);

  useEffect(
    () => () => {
      clearPressTimer();
    },
    [clearPressTimer],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      setPosition(buttonId, clampToPane(clientToCssPixels(event.clientY) - offsetY));
      hasDragged.current = true;
    };

    const handleMouseUp = () => {
      clearPressTimer();
      setIsDragging(false);
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);

    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, offsetY, buttonId, clampToPane, clearPressTimer, setPosition]);

  const onMouseDown = (event: React.MouseEvent) => {
    hasDragged.current = false;
    setOffsetY(clientToCssPixels(event.clientY) - position);
    pressTimer.current = setTimeout(() => setIsDragging(true), LONG_PRESS_MS);
    watchForReleaseOffTheButton();
  };

  const onMouseUp = () => {
    const wasPressPending = clearPressTimer();
    if (wasPressPending && !hasDragged.current && !isDragging) onActivate();
    setIsDragging(false);
  };

  // Mouse presses activate on release so a long press can drag; Enter and Space click with detail 0.
  const onClick = (event: React.MouseEvent) => {
    if (event.detail === 0) onActivate();
  };

  return { position, isDragging, dragHandlers: { onMouseDown, onMouseUp, onClick } };
};
